import { api, extractListItems, extractPaging, extractResponsePaging, normalizePaging, type ApiResponse } from '../client';

export enum DonationRequestStatusEnum {
    PENDING = 'Pending',
    APPROVED = 'Approved',
    REJECTED = 'Rejected',
    PROCESSED = 'Processed',
}

export interface CharityDonationRequest {
    uuid: string;
    name?: string;
    status: string;
    user?: {
        uuid: string;
        email: string;
        full_name?: string;
        first_name?: string;
        last_name?: string;
        credit_card_id?: string;
        department?: string;
    };
    donation_request_status?: {
        uuid: string;
        status: DonationRequestStatusEnum;
        name: string;
    };
    employer?: {
        uuid: string;
        name: string;
        abn?: string;
        contact_email?: string;
        contact_phone?: string;
        contact_name?: string;
        head_office_location?: string;
        leave_hours?: number;
        leave_value?: number;
        updated_at?: string;
    };
    charity?: {
        uuid: string;
        name: string;
        abn?: string;
        charity_government_id?: string;
        contact_email?: string;
        contact_phone?: string;
        head_office_location?: string;
        verified?: boolean;
        verified_acc_name?: string;
        verified_acc_no?: string;
        verified_bsb_no?: string;
    };
    created_at?: string;
    updated_at?: string;
    hours?: number;
    value?: number;
    date?: string;
    charity_name?: string;
    employer_name?: string;
    user_name?: string;
    email?: string;
    leave_hours?: number;
    leave_value?: number;
    charity_received_at?: string | null;
    payment_status?: string;
    /** Canonical lifecycle stage from the backend (App\Support\DonationLifecycle) */
    lifecycle_stage?: string;
    donation_request_status_uuid?: string;
    /** UUID of the payout this donation is in (when processed). Used to link to payout detail. */
    payout_uuid?: string | null;
}

export interface DonationRequestFilters {
    status?: string;
    employer?: string;
    user_uuid?: string;
    employer_uuid?: string;
    charity_uuid?: string;
    search?: string;
    page?: number;
    per_page?: number;
    start_date?: string;
    end_date?: string;
}

export interface PaginationInfo {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number;
    to: number;
}

export interface CharityDonationRequestsResponse {
    success: boolean;
    data: CharityDonationRequest[];
    paging: PaginationInfo;
    message?: string;
}

export interface ApiSuccessResponse<T> {
    success: boolean;
    data: T;
    message?: string;
}

// API base URL
const API_BASE_URL = '/donation-request/charity';

// Hardcoded status options
const STATUSES = [
    { uuid: 'pending', name: DonationRequestStatusEnum.PENDING },
    { uuid: 'approved', name: DonationRequestStatusEnum.APPROVED },
    { uuid: 'rejected', name: DonationRequestStatusEnum.REJECTED },
    { uuid: 'processed', name: DonationRequestStatusEnum.PROCESSED },
];

export const CharityDonationRequestService = {
    /**
     * Get all donation requests with filters and pagination
     */
    getDonationRequests: async (filters: DonationRequestFilters = {}): Promise<{
        donations?: CharityDonationRequest[];
        paging?: PaginationInfo;
        success: boolean;
        error?: any;
    }> => {
        try {
            // Build query parameters
            const queryParams = new URLSearchParams();
            
            if (filters.status && filters.status !== 'all') {
                queryParams.append('donation_request_status_uuid', filters.status);
            }

            if (filters.employer && filters.employer !== 'all') {
                queryParams.append('employer_uuid', filters.employer);
            }

            if (filters.employer_uuid) {
                queryParams.append('employer_uuid', filters.employer_uuid);
            }

            if (filters.user_uuid) {
                queryParams.append('user_uuid', filters.user_uuid);
            }

            if (filters.charity_uuid) {
                queryParams.append('charity_uuid', filters.charity_uuid);
            }

            if (filters.search) {
                queryParams.append('phrase', filters.search);
            }
            if (filters.start_date) {
                queryParams.append('start_date', filters.start_date);
            }
            if (filters.end_date) {
                queryParams.append('end_date', filters.end_date);
            }
            
            queryParams.append('page', filters.page?.toString() || '1');
            queryParams.append('per_page', filters.per_page?.toString() || '10');
            
            // Make API request
            const url = `${API_BASE_URL}?${queryParams.toString()}`;
            const response = await api.get<CharityDonationRequestsResponse>(url);
            
            if (response.data && response.data.success) {
                const items = extractListItems<CharityDonationRequest>(response.data.data);
                const paging = normalizePaging(
                    extractResponsePaging(response) ?? extractPaging(response.data.data),
                    items.length
                ) as PaginationInfo | undefined;
                return {
                    donations: items,
                    paging,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to fetch donation requests'
                };
            }
        } catch (error) {
            console.error('Error fetching charity donation requests:', error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get a single donation request by UUID
     */
    getDonationRequest: async (uuid: string): Promise<{
        donation?: CharityDonationRequest;
        success: boolean;
        error?: any;
    }> => {
        try {
            const url = `/donation-request/${uuid}`;
            const response = await api.get<ApiSuccessResponse<CharityDonationRequest>>(url);
            
            if (response.data && response.data.success) {
                return {
                    donation: response.data.data,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Donation request not found'
                };
            }
        } catch (error) {
            console.error(`Error fetching donation request ${uuid}:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get all available employers for filtering
     * (temporarily returning empty array until API endpoint is available)
     */
    getEmployers: async (): Promise<{
        employers?: Array<{ uuid: string; name: string }>;
        success: boolean;
        error?: any;
    }> => {
        try {
            // Return empty array for now
            // TODO: Uncomment API call when endpoint is ready
            /*
            const url = `${API_BASE_URL}/employers`;
            const response = await api.get<ApiSuccessResponse<Array<{ uuid: string; name: string }>>>(url);

            if (response.data && response.data.success) {
                return {
                    employers: response.data.data,
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to fetch employers'
                };
            }
            */
            return {
                employers: [],
                success: true
            };
        } catch (error) {
            console.error('Error fetching employers:', error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Get all available statuses for filtering (using hardcoded values)
     */
    getStatuses: async (): Promise<{
        statuses?: Array<{ uuid: string; name: DonationRequestStatusEnum }>;
        success: boolean;
        error?: any;
    }> => {
        try {
            // Return hardcoded statuses instead of making an API call
            return {
                statuses: STATUSES,
                success: true
            };
        } catch (error) {
            console.error('Error fetching statuses:', error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Update donation request status
     */
    updateDonationRequestStatus: async (uuid: string, status: DonationRequestStatusEnum): Promise<{
        success: boolean;
        error?: any;

    }> => {
        try {
            const url = `${API_BASE_URL}/${uuid}/status`;
            const response = await api.patch<ApiSuccessResponse<{ success: boolean }>>(url, { status });

            if (response.data && response.data.success) {
                return {
                    success: true
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to update status'
                };
            }
        } catch (error) {
            console.error(`Error updating donation request ${uuid}:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Mark a donation as received by the charity
     */
    markAsReceived: async (uuid: string): Promise<{
        success: boolean;
        donation?: CharityDonationRequest;
        error?: any;
    }> => {
        try {
            const url = `/donation-request/${uuid}/mark-received`;
            const response = await api.put<ApiSuccessResponse<CharityDonationRequest>>(url);

            if (response.data && response.data.success) {
                return {
                    success: true,
                    donation: response.data.data
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to mark as received'
                };
            }
        } catch (error) {
            console.error(`Error marking donation ${uuid} as received:`, error);
            return {
                success: false,
                error
            };
        }
    },

    /**
     * Resend the donation receipt email to the employee.
     */
    resendReceipt: async (uuid: string): Promise<{
        success: boolean;
        message?: string;
        error?: any;
    }> => {
        try {
            const url = `/donation-request/${uuid}/resend-receipt`;
            const response = await api.post<ApiSuccessResponse<null>>(url);

            if (response.data && response.data.success) {
                return {
                    success: true,
                    message: response.data.message
                };
            } else {
                return {
                    success: false,
                    error: response.data?.message || 'Failed to resend receipt'
                };
            }
        } catch (error) {
            console.error(`Error resending receipt for donation ${uuid}:`, error);
            return {
                success: false,
                error
            };
        }
    }
};